/* =====================================================================
 * RunList — 진행 상태 / 최근 기록 목록.
 *
 * 각 행은 "무엇에 대한 작업이었나"를 제목으로 보여주고, 그 대화·문서로
 * 바로 갈 수 있는 이동 버튼을 함께 둔다. 제목은 서버가 워크스페이스
 * 아이템 이름에서 채워 준다 — 통합 에이전트는 첫 질문에서 자동 생성된
 * 대화 제목, 문서 에이전트는 문서명이라 한 필드로 둘 다 커버된다.
 *
 * Status is never colour-alone: every row carries an icon and a text
 * label alongside the tone, so it survives colourblindness, greyscale
 * printing, and forced-colours mode.
 * ===================================================================== */

import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import {
  AlertCircle,
  ArrowUpRight,
  Ban,
  CheckCircle2,
  Clock,
  Loader2,
  MinusCircle,
} from 'lucide-react';
import type { AgentRun, AgentRunStatus } from '@auw/api-client';
import type { WorkspaceMode } from '@auw/types';
import { modeToPathWithId } from '@auw/workspace-utils';

import { EmptyState } from './dashboard.styles';
import { formatDashboardTokenCount, runDisplayQuery } from './dashboardMetrics';

/**
 * Backend workspace types → the route that owns them.
 *
 * The backend distinguishes a document run by format (`docx-agent`,
 * `xlsx-agent`, …) but the frontend has a single `/doc-agent/:id`
 * route, so the formats collapse onto it. An unmapped type yields no
 * link rather than a guessed one — a button that navigates somewhere
 * wrong is worse than no button.
 */
const ROUTE_MODE_BY_WORKSPACE: Record<string, WorkspaceMode> = {
  agent: 'agent',
  'doc-agent': 'doc-agent',
  'docx-agent': 'doc-agent',
  'xlsx-agent': 'doc-agent',
  'ppt-agent': 'doc-agent',
  'pdf-agent': 'doc-agent',
};

const List = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
`;

const Row = styled.li`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 2px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.borderLight};
  font-size: 0.8125rem;
  min-width: 0;

  &:last-child {
    border-bottom: none;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    /* Two rows on a phone: status + title, then meta + action. Squeezing
       them onto one line would truncate the title to nothing. */
    flex-wrap: wrap;
    gap: 8px 10px;
    padding: 12px 2px;
  }
`;

/** Theme colour slots a status may use. Keeps tones on the token layer. */
type Tone = 'textSecondary' | 'textMuted' | 'info' | 'success' | 'error';

const StatusCell = styled.span<{ $tone: Tone }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-width: 88px;
  color: ${({ theme, $tone }) => (theme.colors as Record<Tone, string>)[$tone]};
  font-weight: 500;
  flex-shrink: 0;

  svg {
    width: 14px;
    height: 14px;
    flex-shrink: 0;
  }
`;

const spin = keyframes`
  to { transform: rotate(360deg); }
`;

const Spinner = styled(Loader2)`
  animation: ${spin} 1s linear infinite;
`;

const Body = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    /* Title drops to its own line so it is never squeezed to an
       ellipsis by the status/time/action row above it. */
    flex-basis: 100%;
    order: 4;
  }
`;

const Title = styled.span`
  color: ${({ theme }) => theme.colors.textPrimary};
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const UntitledTitle = styled(Title)`
  color: ${({ theme }) => theme.colors.textMuted};
  font-style: italic;
`;

const Workspace = styled.span`
  color: ${({ theme }) => theme.colors.textMuted};
  font-size: 0.75rem;
`;

const TokenUsage = styled.span`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px 10px;
  color: ${({ theme }) => theme.colors.textMuted};
  font-size: 0.6875rem;
  font-variant-numeric: tabular-nums;
`;

const TokenPart = styled.span`
  display: inline-flex;
  align-items: baseline;
  gap: 4px;

  & + &::before {
    content: '|';
    color: ${({ theme }) => theme.colors.border};
    margin-right: 6px;
  }
`;

const TokenLabel = styled.span`
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const Meta = styled.span`
  color: ${({ theme }) => theme.colors.textMuted};
  font-variant-numeric: tabular-nums;
  flex-shrink: 0;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    order: 2;
    margin-left: auto;
  }
`;

/* Mobile reading order per row:
 *   line 1  [status] ............ [time] [open]
 *   line 2  [title / workspace]
 * The action sits at the far right of line 1 rather than between the
 * status and the time, where it interrupts the sentence the row makes. */

const GoButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
  border: 1px solid ${({ theme }) => theme.colors.border};
  background: transparent;
  color: ${({ theme }) => theme.colors.textSecondary};
  border-radius: ${({ theme }) => theme.sizes.borderRadiusSm};
  padding: 5px 10px;
  font-size: 0.75rem;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease, border-color 0.15s ease;
  white-space: nowrap;

  svg {
    width: 13px;
    height: 13px;
  }

  &:hover {
    background: ${({ theme }) => theme.colors.bgHover};
    color: ${({ theme }) => theme.colors.textPrimary};
    border-color: ${({ theme }) => theme.colors.borderInput};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    /* Comfortable touch target. */
    padding: 7px 12px;
    order: 3;
  }
`;

export interface RunListProps {
  runs: AgentRun[];
  mode: 'active' | 'recent';
  labelFor: (workspaceType: string) => string;
  emptyText: string;
}

const STATUS_META: Record<
  AgentRunStatus,
  { icon: React.ComponentType; key: string; tone: Tone }
> = {
  queued: { icon: Clock, key: 'common:dashboard.statusQueued', tone: 'textSecondary' },
  running: { icon: Spinner, key: 'common:dashboard.statusRunning', tone: 'info' },
  done: { icon: CheckCircle2, key: 'common:dashboard.statusDone', tone: 'success' },
  failed: { icon: AlertCircle, key: 'common:dashboard.statusFailed', tone: 'error' },
  cancelled: { icon: Ban, key: 'common:dashboard.statusCancelled', tone: 'textMuted' },
  expired: { icon: MinusCircle, key: 'common:dashboard.statusExpired', tone: 'textMuted' },
};

function formatDuration(ms: number): string {
  if (ms <= 0) return '—';
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)}s`;
  return `${Math.round(ms / 60_000)}m`;
}

export const RunList: React.FC<RunListProps> = ({
  runs,
  mode,
  labelFor,
  emptyText,
}) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const timeFormatter = useMemo(
    () =>
      new Intl.DateTimeFormat(i18n.language, {
        month: 'numeric',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      }),
    [i18n.language],
  );

  const targetPath = useCallback((run: AgentRun): string | null => {
    const routeMode = ROUTE_MODE_BY_WORKSPACE[run.workspace_type];
    if (!routeMode || !run.item_id) return null;
    return modeToPathWithId(routeMode, run.item_id);
  }, []);

  if (!runs.length) return <EmptyState>{emptyText}</EmptyState>;

  return (
    <List>
      {runs.map((run) => {
        const meta = STATUS_META[run.status] ?? STATUS_META.done;
        const Icon = meta.icon;
        const path = targetPath(run);
        const workspaceLabel = labelFor(run.workspace_type);
        const turnQuery = runDisplayQuery(run);
        const itemContext = run.item_title && run.item_title !== turnQuery
          ? `${workspaceLabel} · ${run.item_title}`
          : workspaceLabel;
        return (
          <Row key={run.run_id}>
            <StatusCell $tone={meta.tone}>
              <Icon aria-hidden />
              {t(meta.key)}
            </StatusCell>

            <Body>
              {turnQuery ? (
                <Title title={turnQuery}>{turnQuery}</Title>
              ) : (
                <UntitledTitle>{t('common:dashboard.untitled')}</UntitledTitle>
              )}
              <Workspace>{itemContext}</Workspace>
              {mode === 'recent' && (
                <TokenUsage>
                  <TokenPart
                    title={run.reasoning_token_count == null
                      ? undefined
                      : run.reasoning_token_count.toLocaleString(i18n.language)}
                  >
                    <TokenLabel>{t('common:dashboard.tokenReasoning')}</TokenLabel>
                    {formatDashboardTokenCount(run.reasoning_token_count)}
                  </TokenPart>
                  <TokenPart
                    title={run.input_token_count == null
                      ? undefined
                      : run.input_token_count.toLocaleString(i18n.language)}
                  >
                    <TokenLabel>{t('common:dashboard.tokenInput')}</TokenLabel>
                    {formatDashboardTokenCount(run.input_token_count)}
                  </TokenPart>
                  <TokenPart
                    title={run.output_token_count == null
                      ? undefined
                      : run.output_token_count.toLocaleString(i18n.language)}
                  >
                    <TokenLabel>{t('common:dashboard.tokenOutput')}</TokenLabel>
                    {formatDashboardTokenCount(run.output_token_count)}
                  </TokenPart>
                </TokenUsage>
              )}
            </Body>

            <Meta>
              {mode === 'active'
                ? timeFormatter.format(new Date(run.enqueued_at))
                : `${formatDuration(run.duration_ms)} · ${timeFormatter.format(
                    new Date(run.enqueued_at),
                  )}`}
            </Meta>

            {path && (
              <GoButton
                type="button"
                onClick={() => navigate(path)}
                aria-label={`${turnQuery ?? workspaceLabel} — ${t(
                  'common:dashboard.goTo',
                )}`}
              >
                {t('common:dashboard.goTo')}
                <ArrowUpRight aria-hidden />
              </GoButton>
            )}
          </Row>
        );
      })}
    </List>
  );
};
